import { TypedDocumentNode, gql } from '@apollo/client';
import algoliasearch from 'algoliasearch';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import {
  AUCTION_MARKET_URL,
  INSTANT_BUY_URL,
} from '@sorare/core/src/constants/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { joinFiltersWithAnd, sportFilter } from '@sorare/core/src/lib/algolia';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PromotionalEvent_PromotionalEvent } from './__generated__/index.graphql';

const Background = styled(Vertical).attrs({ center: true })`
  justify-content: flex-end;
  height: 370px;
  flex: 1;
  flex-shrink: 0;
  min-width: clamp(0px, 100%, 400px);
  padding: var(--double-unit);
  border-radius: var(--unit);
  background-color: var(--c-neutral-200);
  background-image: var(--logo), var(--background);
  background-size: contain, cover;
  background-position: center, center;
  @media ${tabletAndAbove} {
    background-size:
      auto 300px,
      cover;
    background-position:
      center top,
      center;
  }
  background-repeat: no-repeat;

  position: relative;
`;

const PositionnedTimeLeft = styled.div`
  position: absolute;
  right: var(--double-unit);
  top: var(--double-unit);
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 1);
`;

const Action = styled(Horizontal).attrs({ gap: 2, center: true })`
  flex-shrink: 0;
`;

type Props = {
  promotionalEvent: PromotionalEvent_PromotionalEvent;
};

const useHasInstantBuyCards = (sport?: Sport, algoliaFilters?: string) => {
  const [hasInstantBuyCards, setHasInstantBuyCards] = useState<boolean>(false);
  const {
    algoliaCardIndexes: { 'Cards On Sale Ending Soon': cardIndexName },
    algoliaApplicationId,
    algoliaSearchApiKey,
  } = useConfigContext();

  useEffect(() => {
    const client = algoliasearch(algoliaApplicationId, algoliaSearchApiKey);
    const cardsIndex = client.initIndex(cardIndexName);
    const filters = joinFiltersWithAnd([
      'sale.primary:true',
      'sale.type:PrimaryOffer',
      'sale.bundled:false',
      sport && sportFilter(sport),
      'NOT rarity:common',
      algoliaFilters,
    ]);

    cardsIndex
      .search('', { filters, attributesToRetrieve: [] })
      .then(response => {
        setHasInstantBuyCards(response.nbHits > 0);
      });
  }, [
    algoliaApplicationId,
    algoliaFilters,
    algoliaSearchApiKey,
    cardIndexName,
    sport,
  ]);

  return hasInstantBuyCards;
};

export const PromotionalEvent = ({ promotionalEvent }: Props) => {
  const { sport } = useSportContext();

  const { pictureUrl, logoUrl, blogUrl, slug, algoliaFilters } =
    promotionalEvent;
  const instantBuyHasResults = useHasInstantBuyCards(sport, algoliaFilters);

  if (!sport) {
    return null;
  }

  const tab = instantBuyHasResults
    ? INSTANT_BUY_URL[sport]
    : AUCTION_MARKET_URL[sport];

  return (
    <Background
      style={
        {
          '--background': `url(${pictureUrl})`,
          '--logo': `url(${logoUrl})`,
        } as React.CSSProperties
      }
    >
      <PositionnedTimeLeft>
        <TimeLeft time={promotionalEvent.endDate} />
      </PositionnedTimeLeft>
      <Action>
        <Button
          size="large"
          color="secondary"
          to={generatePath(
            `${tab}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${slug}#market`
          )}
        >
          <FormattedMessage {...glossary.buy} />
        </Button>
        {blogUrl && (
          <Button href={blogUrl} size="large" color="tertiary">
            <FormattedMessage {...glossary.learnMore} />
          </Button>
        )}
      </Action>
    </Background>
  );
};

PromotionalEvent.fragments = {
  PromotionalEvent: gql`
    fragment PromotionalEvent_PromotionalEvent on PromotionalEventInterface {
      slug
      pictureUrl
      logoUrl
      blogUrl
      endDate
      algoliaFilters
    }
  ` as TypedDocumentNode<PromotionalEvent_PromotionalEvent>,
};
